import { BOWL_SOUNDS } from './audio';
import {
  MAX_DURATION_MS,
  MAX_INTERVAL_MS,
  MIN_DURATION_MS,
  MIN_INTERVAL_MS,
  minutesToMs
} from './timer';
import type { UserPreferences } from './types';

const STORAGE_KEY = 'tibetan-bowl-timer.preferences.v1';

export const DEFAULT_PREFERENCES: UserPreferences = {
  intervalMs: minutesToMs(5),
  durationMode: 'open',
  durationMs: null,
  soundId: BOWL_SOUNDS[0].id,
  volume: 0.68,
  notificationsEnabled: false,
  wakeLockEnabled: false
};

export function loadPreferences(): UserPreferences {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(raw) as Partial<UserPreferences>;
    return sanitizePreferences(parsed);
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

export function savePreferences(preferences: UserPreferences): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitizePreferences(preferences)));
  } catch {
    // Storage may be unavailable in private or restricted browsing. Timer use continues.
  }
}

export function sanitizePreferences(input: Partial<UserPreferences>): UserPreferences {
  const intervalMs =
    typeof input.intervalMs === 'number' &&
    input.intervalMs >= MIN_INTERVAL_MS &&
    input.intervalMs <= MAX_INTERVAL_MS
      ? Math.round(input.intervalMs)
      : DEFAULT_PREFERENCES.intervalMs;

  const durationMode = input.durationMode === 'fixed' ? 'fixed' : 'open';
  const durationMs =
    durationMode === 'fixed' &&
    typeof input.durationMs === 'number' &&
    input.durationMs >= MIN_DURATION_MS &&
    input.durationMs <= MAX_DURATION_MS
      ? Math.round(input.durationMs)
      : null;

  const soundId = BOWL_SOUNDS.some((sound) => sound.id === input.soundId)
    ? String(input.soundId)
    : DEFAULT_PREFERENCES.soundId;

  const volume =
    typeof input.volume === 'number' && Number.isFinite(input.volume)
      ? Math.min(1, Math.max(0, input.volume))
      : DEFAULT_PREFERENCES.volume;

  return {
    intervalMs,
    durationMode,
    durationMs,
    soundId,
    volume,
    notificationsEnabled: input.notificationsEnabled === true,
    wakeLockEnabled: input.wakeLockEnabled === true
  };
}
